import React, { useState, useEffect } from 'react';
import { Search, Download, AlertCircle, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/apiClient.ts';

const MessageReports = () => {
  const { isArabic } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Fetch messages
  const fetchMessages = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        per_page: perPage,
        page: page,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await apiClient.get(`/v1/reports/messages?${params}`);
      setMessages(response.data.data || []);
      setCurrentPage(response.data.meta?.current_page || 1);
      setTotalPages(response.data.meta?.last_page || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'خطأ في تحميل الرسائل');
      setToast({ type: 'error', message: isArabic ? 'فشل تحميل الرسائل' : 'Failed to load messages' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary statistics
  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await apiClient.get(`/v1/reports/messages/summary?${params}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  // Load data on mount and filter changes
  useEffect(() => {
    fetchMessages(1);
    fetchSummary();
  }, [searchQuery, statusFilter, fromDate, toDate]);

  // Status badge colors
  const getStatusColor = (status) => {
    const colors = {
      'sent': 'bg-blue-100 text-blue-800',
      'delivered': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'sent': 'مرسلة',
      'delivered': 'تم التسليم',
      'failed': 'فشلت',
      'pending': 'قيد الانتظار',
    };
    return labels[status] || status;
  };

  // Export to CSV
  const exportToCSV = () => {
    if (messages.length === 0) {
      setToast({ type: 'error', message: 'لا توجد بيانات للتصدير' });
      return;
    }

    const headers = ['المسافر', 'الهاتف', 'الرسالة', 'الحالة', 'تاريخ الإرسال', 'الوقت'];
    const rows = messages.map(msg => [
      msg.traveler_name,
      msg.phone_number,
      msg.message_body.substring(0, 50),
      getStatusLabel(msg.status),
      msg.scheduled_for?.split('T')[0] || '-',
      msg.sent_at?.split('T')[1]?.substring(0, 5) || '-',
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `تقرير_الرسائل_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    setToast({ type: 'success', message: 'تم تصدير البيانات بنجاح' });
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-6 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">تقرير الرسائل</h1>
          <p className="text-gray-600">عرض وتتبع جميع الرسائل المرسلة عبر Twilio</p>
        </div>

        {/* Statistics Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">إجمالي الرسائل</div>
              <div className="text-2xl font-bold text-gray-900">{summary.total_messages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">مرسلة</div>
              <div className="text-2xl font-bold text-blue-600">{summary.sent}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">تم التسليم</div>
              <div className="text-2xl font-bold text-green-600">{summary.delivered}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">فشلت</div>
              <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">معدل النجاح</div>
              <div className="text-2xl font-bold text-green-600">{summary.success_rate}%</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">التصفية</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">بحث</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="اسم أو هاتف..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">الحالة</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">الكل</option>
                <option value="sent">مرسلة</option>
                <option value="delivered">تم التسليم</option>
                <option value="failed">فشلت</option>
                <option value="pending">قيد الانتظار</option>
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">من</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">إلى</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Export Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Download className="w-4 h-4" />
              تصدير CSV
            </button>
          </div>
        </div>

        {/* Messages Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="p-6 flex items-center gap-3 bg-red-50">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-red-800 font-medium">خطأ</p>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>لا توجد رسائل</p>
            </div>
          ) : (
            <>
              <div className="app-table-wrap">
                <table className="app-table w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">المسافر</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الهاتف</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الرسالة</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الحالة</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">التاريخ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {messages.map((msg) => (
                      <tr key={msg.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-right text-sm text-gray-900">{msg.traveler_name}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600 font-mono">{msg.phone_number}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600 max-w-xs truncate">{msg.message_body}</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(msg.status)}`}>
                            {getStatusLabel(msg.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600">
                          {msg.sent_at ? new Date(msg.sent_at).toLocaleDateString('ar-SA') : msg.scheduled_for?.split('T')[0] || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    الصفحة {currentPage} من {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchMessages(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      السابق
                    </button>
                    <button
                      onClick={() => fetchMessages(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      التالي
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-4 ${isArabic ? 'right-4' : 'left-4'} max-w-sm p-4 rounded-lg text-white text-sm shadow-lg transition-opacity ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default MessageReports;
